import React, { useState, useRef, useImperativeHandle, forwardRef, ForwardedRef } from 'react';

import classNames from 'classnames';
import ReactModal from 'react-modal';
import { Icon } from 'src/common/components/Icon';
import { OrbitIcons } from 'src/common/constants.';

import {
  createFreeSwitchGroup,
  fetchFreeSwitchGroupStatus,
  ALLOWED_SHARED_INSTANCE_TYPES,
  CreateFreeSwitchGroupResponse,
  FreeSwitchGroupStatusResponse,
} from '../api';

export interface CreateFreeSwitchGroupModalRef {
  show: () => void;
  hide: () => void;
}

interface CreateFreeSwitchGroupModalProps {
  onGroupCreated: () => void;
}

type ModalStep = 'form' | 'provisioning' | 'success' | 'error';

const POLL_INTERVAL_MS = 10000; // 10 seconds

export const CreateFreeSwitchGroupModal = forwardRef(
  (
    { onGroupCreated }: CreateFreeSwitchGroupModalProps,
    ref: ForwardedRef<CreateFreeSwitchGroupModalRef>
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [step, setStep] = useState<ModalStep>('form');
    const [name, setName] = useState('');
    const [instanceType, setInstanceType] = useState<string>('t3.large');
    const [wizardEnabled, setWizardEnabled] = useState(false);
    const [maxAccounts, setMaxAccounts] = useState(25);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Provisioning state
    const [createResponse, setCreateResponse] = useState<CreateFreeSwitchGroupResponse | null>(
      null
    );
    const [statusResponse, setStatusResponse] = useState<FreeSwitchGroupStatusResponse | null>(
      null
    );
    const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

    const inputRef = useRef<HTMLInputElement>(null);

    const resetState = (): void => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      setIsOpen(false);
      setStep('form');
      setName('');
      setInstanceType('t3.large');
      setWizardEnabled(false);
      setMaxAccounts(25);
      setIsSubmitting(false);
      setError(null);
      setCreateResponse(null);
      setStatusResponse(null);
    };

    useImperativeHandle(ref, () => ({
      show: () => {
        resetState();
        setIsOpen(true);
        setTimeout(() => inputRef.current?.focus(), 100);
      },
      hide: resetState,
    }));

    const closeModal = (): void => {
      if (isSubmitting) return;
      if (step === 'provisioning') {
        // Allow closing during provisioning but warn
        if (
          !window.confirm(
            'El stack se sigue creando en AWS. ¿Seguro que quieres cerrar? Puedes ver el progreso en la consola de AWS.'
          )
        ) {
          return;
        }
      }
      resetState();
      if (step === 'success') {
        onGroupCreated();
      }
    };

    const startPolling = (groupId: number): void => {
      const poll = async (): Promise<void> => {
        try {
          const status = await fetchFreeSwitchGroupStatus(groupId);
          setStatusResponse(status);

          if (status.succeeded) {
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }
            setStep('success');
          } else if (status.failed) {
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }
            setError(status.failure_reasons?.[0] || 'El stack falló durante la creación');
            setStep('error');
          }
        } catch (err) {
          // Don't stop polling on transient errors
          console.error('Error polling status:', err);
        }
      };

      // Initial poll
      poll();

      // Set up interval
      pollIntervalRef.current = setInterval(() => void poll(), POLL_INTERVAL_MS);
    };

    const handleSubmit = async (): Promise<void> => {
      if (!name.trim()) {
        setError('El nombre es requerido');
        return;
      }

      // Validate name format
      if (!/^[a-z][a-z0-9_]{2,29}$/.test(name)) {
        setError(
          'El nombre debe empezar con letra minúscula, solo contener letras, números y guiones bajos (3-30 caracteres)'
        );
        return;
      }

      setIsSubmitting(true);
      setError(null);

      try {
        const response = await createFreeSwitchGroup({
          name,
          instance_type: instanceType as 't3.large' | 'c5.large' | 'c5.xlarge' | 'c5.2xlarge',
          wizard_enabled: wizardEnabled,
          max_accounts: maxAccounts,
        });
        setCreateResponse(response);
        setStep('provisioning');
        startPolling(response.group_id);
      } catch (err) {
        const axiosErr = err as {
          response?: { data?: { detail?: string | { message?: string } } };
        };
        const detail = axiosErr?.response?.data?.detail;
        const message =
          typeof detail === 'string' ? detail : detail?.message || 'Error al crear el grupo';
        setError(message);
      } finally {
        setIsSubmitting(false);
      }
    };

    const handleDone = (): void => {
      onGroupCreated();
      resetState();
    };

    return (
      <ReactModal
        isOpen={isOpen}
        onRequestClose={closeModal}
        className="modal modal-sm"
        overlayClassName="overlay"
        appElement={document.querySelector('#root') as HTMLElement}
      >
        {step === 'form' && (
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-bold text-black-1.0">Crear FreeSWITCH Group</h2>
              <p className="mt-1 text-sm text-gray-2.0">
                Un grupo compartido permite que múltiples cuentas pequeñas usen la misma instancia
                de FreeSWITCH, reduciendo costos de infraestructura.
              </p>
            </div>

            {error && (
              <div className="bg-red-50 p-3 text-sm text-red-800 border border-red-200 rounded-lg">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-black-1.0">
                Nombre del grupo <span className="text-red-500">*</span>
              </label>
              <input
                ref={inputRef}
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value.toLowerCase())}
                placeholder="shared_small_accounts"
                disabled={isSubmitting}
                className={classNames(
                  'mt-1 w-full rounded-lg border px-3 py-2 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-primary-2.0 focus:border-primary-2.0',
                  'border-white-2.0 bg-white-0.0'
                )}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && name.trim()) {
                    handleSubmit();
                  }
                }}
              />
              <p className="mt-1 text-xs text-gray-2.0">
                Letras minúsculas, números y guiones bajos (3-30 caracteres)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-black-1.0">Tipo de instancia</label>
              <select
                value={instanceType}
                onChange={(e) => setInstanceType(e.target.value)}
                disabled={isSubmitting}
                className={classNames(
                  'mt-1 w-full rounded-lg border px-3 py-2 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-primary-2.0 focus:border-primary-2.0',
                  'border-white-2.0 bg-white-0.0'
                )}
              >
                {ALLOWED_SHARED_INSTANCE_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-2.0">
                Instancias más grandes soportan más cuentas concurrentes
              </p>
            </div>

            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-black-1.0">Max cuentas</label>
                <input
                  type="number"
                  value={maxAccounts}
                  onChange={(e) => setMaxAccounts(Number(e.target.value))}
                  min={1}
                  max={100}
                  disabled={isSubmitting}
                  className={classNames(
                    'mt-1 w-full rounded-lg border px-3 py-2 text-sm',
                    'focus:outline-none focus:ring-2 focus:ring-primary-2.0 focus:border-primary-2.0',
                    'border-white-2.0 bg-white-0.0'
                  )}
                />
              </div>
              <div className="flex items-end flex-1 pb-2">
                <label className="flex items-center gap-2 text-sm text-black-1.0 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={wizardEnabled}
                    onChange={(e) => setWizardEnabled(e.target.checked)}
                    disabled={isSubmitting}
                    className="rounded border-gray-300 text-primary-2.0 focus:ring-primary-2.0 w-4 h-4"
                  />
                  Habilitar para Wizard
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={closeModal}
                disabled={isSubmitting}
                className="btn btn-ghost"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!name.trim() || isSubmitting}
                className="btn btn-primary flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <span className="border-t-transparent animate-spin w-4 h-4 border-2 border-white rounded-full" />
                    Creando...
                  </>
                ) : (
                  <>
                    <Icon name={OrbitIcons.PlusCircle} size={16} />
                    Crear Grupo
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {step === 'provisioning' && (
          <div className="space-y-4">
            <div className="text-center">
              <div className="flex items-center justify-center w-16 h-16 mx-auto bg-blue-100 rounded-full">
                <span className="border-t-transparent animate-spin w-8 h-8 border-4 border-blue-600 rounded-full" />
              </div>
              <h2 className="mt-4 text-lg font-bold text-black-1.0">
                Provisionando infraestructura
              </h2>
              <p className="mt-1 text-sm text-gray-2.0">
                Creando instancia EC2, configurando EFS y preparando FreeSWITCH...
              </p>
            </div>

            {createResponse && (
              <div className="bg-gray-50 p-4 space-y-2 font-mono text-xs border rounded-lg">
                <div className="flex justify-between">
                  <span className="text-gray-500">Stack:</span>
                  <span className="text-black-1.0">{createResponse.stack_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Estado:</span>
                  <span className="text-blue-600">
                    {statusResponse?.status || createResponse.status}
                  </span>
                </div>
                {statusResponse?.instance_id && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Instancia:</span>
                    <span className="text-black-1.0">{statusResponse.instance_id}</span>
                  </div>
                )}
              </div>
            )}

            <p className="text-xs text-center text-gray-2.0">
              Este proceso puede tardar 5-10 minutos. Puedes cerrar esta ventana y el proceso
              continuará en segundo plano.
            </p>

            {createResponse && (
              <a
                href={createResponse.console_url}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline block text-xs text-center text-blue-600"
              >
                Ver progreso en AWS Console →
              </a>
            )}

            <div className="flex justify-center pt-2">
              <button type="button" onClick={closeModal} className="btn btn-ghost">
                Cerrar (continúa en segundo plano)
              </button>
            </div>
          </div>
        )}

        {step === 'success' && statusResponse && (
          <div className="space-y-4">
            <div className="text-center">
              <div className="flex items-center justify-center w-16 h-16 mx-auto bg-green-100 rounded-full">
                <Icon name={OrbitIcons.CheckCircle2} size={32} className="text-green-600" />
              </div>
              <h2 className="mt-4 text-lg font-bold text-black-1.0">Grupo creado exitosamente</h2>
              <p className="mt-1 text-sm text-gray-2.0">
                El FreeSWITCH compartido está listo para recibir cuentas.
              </p>
            </div>

            <div className="bg-gray-50 p-4 space-y-2 font-mono text-xs border rounded-lg">
              <div className="flex justify-between">
                <span className="text-gray-500">Nombre:</span>
                <span className="font-semibold text-black-1.0">{statusResponse.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">FreeSWITCH URI:</span>
                <span className="text-black-1.0">{statusResponse.freeswitch_uri}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Instancia EC2:</span>
                <span className="text-black-1.0">{statusResponse.instance_id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">IP Privada:</span>
                <span className="text-black-1.0">{statusResponse.instance_private_ip}</span>
              </div>
            </div>

            <div className="flex justify-center pt-2">
              <button type="button" onClick={handleDone} className="btn btn-primary">
                Listo
              </button>
            </div>
          </div>
        )}

        {step === 'error' && (
          <div className="space-y-4">
            <div className="text-center">
              <div className="flex items-center justify-center w-16 h-16 mx-auto bg-red-100 rounded-full">
                <Icon name={OrbitIcons.AlertCircle} size={32} className="text-red-600" />
              </div>
              <h2 className="mt-4 text-lg font-bold text-black-1.0">Error al crear el grupo</h2>
              <p className="mt-1 text-sm text-gray-2.0">
                La creación del stack de CloudFormation falló.
              </p>
            </div>

            {error && (
              <div className="bg-red-50 p-3 text-sm text-red-800 border border-red-200 rounded-lg">
                {error}
              </div>
            )}

            {createResponse && (
              <a
                href={createResponse.console_url}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline block text-xs text-center text-blue-600"
              >
                Ver detalles en AWS Console →
              </a>
            )}

            <div className="flex justify-center gap-3 pt-2">
              <button type="button" onClick={closeModal} className="btn btn-ghost">
                Cerrar
              </button>
            </div>
          </div>
        )}
      </ReactModal>
    );
  }
);
